<template>
    <div>
        <hero-cards :cards="heroCards"></hero-cards>

        <h2
            class="text-center"
            style="margin: 3.5rem 0 1.1rem"
            id="itb-composition-title"
        >
            {{ $t("epdm.composition") }}
        </h2>
        <p class="h3 text-center">{{ $t("epdm.highest_quality_materials") }}</p>
        <section class="itb-hero-cards-simple">
            <div class="container">
                <div class="row d-flex align-content-center mx-0">
                    <div class="col-12 p-0">
                        <div
                            class="card"
                            id="itb-first-epdm-card"
                            v-lazy-container="{ selector: '.itb-lazy' }"
                        >
                            <img
                                data-src="/images/articles/sbr.webp"
                                alt="sbr"
                                class="itb-lazy"
                            />
                            <div class="card-body">
                                <h3>
                                    {{ $t("epdm.first_block_title") }}
                                </h3>
                                <p class="m-0">
                                    {{ $t("epdm.first_block_description") }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="row d-flex align-content-center mx-0">
                    <div class="col-12 p-0">
                        <div
                            class="card"
                            id="itb-second-epdm-card"
                            v-lazy-container="{ selector: '.itb-lazy' }"
                        >
                            <img
                                data-src="/images/articles/epdm.webp"
                                alt="epdm"
                                class="itb-lazy"
                            />
                            <div class="card-body">
                                <h3>
                                    {{ $t("epdm.second_block_title") }}
                                </h3>
                                <p class="m-0">
                                    {{ $t("epdm.second_block_description") }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="row d-flex align-content-center mx-0">
                    <div class="col-12 p-0">
                        <div
                            class="card"
                            id="itb-third-epdm-card"
                            v-lazy-container="{ selector: '.itb-lazy' }"
                        >
                            <img
                                data-src="/images/articles/lepidlo.webp"
                                alt="epdm"
                                class="itb-lazy"
                            />
                            <div class="card-body">
                                <h3>
                                    {{ $t("epdm.third_block_title") }}
                                </h3>
                                <p class="m-0">
                                    {{ $t("epdm.third_block_description") }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section>
            <div class="container">
                <div class="row">
                    <div
                        class="col-12 d-flex flex-column align-items-center itb-youtube-video"
                    >
                        <h3 class="text-center">
                            {{ $t("epdm.first_video_title") }}
                        </h3>
                        <div
                            class=" d-flex justify-content-center align-items-center itb-youtube-warning-wrap"
                        >
                            <p
                                class="mb-0 itb-youtube-warning"
                                style="font-size:1.71rem; max-width:1180px;"
                                :class="{
                                    'itb-youtube-warning-highlight': isYoutubeHighlighted
                                }"
                            >
                                {{ $t("general.youtube_warning") }}
                                <a
                                    href="https://policies.google.com/privacy"
                                    target="_blank"
                                    >{{ $t("general.youtube_policy") }}.</a
                                >
                            </p>
                            <button
                                class="btn btn-secondary ml-2"
                                :disabled="youtubePermit"
                                @click="youtubePermit = true"
                            >
                                {{ $t("cookies.accept") }}
                            </button>
                        </div>
                        <div
                            v-if="!youtubePermit"
                            class="itb-youtube-image d-flex justify-content-center align-items-center"
                            @click="warningHighlight"
                        >
                            <img
                                src="/images/youtube-logo.png"
                                alt="youtube-logo"
                            />
                        </div>
                        <iframe
                            :src="pageParams.page_first_video_url"
                            frameborder="0"
                            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                            allowfullscreen
                            v-else
                        ></iframe>
                    </div>
                </div>
            </div>
        </section>

        <section
            class="itb-epdm-colors itb-horizontal-border text-center"
            id="itb-epdm-colors-section"
        >
            <h2>{{ $t("epdm.color_options_epdm") }}</h2>
            <div class="container">
                <div v-for="category in surfaceCategories[1]">
                    <h3>{{ $t("constants." + category.data.name) }}</h3>
                    <p
                        class="itb-h3-subtitle"
                        v-if="category.data.subtitle.length"
                        v-html="$t('constants.' + category.data.subtitle)"
                    ></p>
                    <div
                        class="itb-epdm-colors-wrapper d-flex justify-content-center flex-wrap mx-auto"
                    >
                        <div
                            class="itb-epdm-color"
                            v-for="surface in category['surfaces']"
                        >
                            <div
                                class="itb-bg itb-lazy-loading-bg"
                                v-lazy:background-image="
                                    surface.image
                                        ? '/uploads/' +
                                          surface.image.path +
                                          '/' +
                                          surface.image.name
                                        : null
                                "
                            ></div>
                            <div class="itb-text">{{ surface.name }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section
            class="itb-epdm-colors itb-horizontal-border text-center"
            id="itb-mulch-colors-section"
        >
            <h2>{{ $t("epdm.color_options_mulch") }}</h2>
            <div class="container">
                <div v-for="category in surfaceCategories[2]">
                    <h3>{{ $t("constants." + category.data.name) }}</h3>
                    <p
                        class="itb-h3-subtitle"
                        v-if="category.data.subtitle.length"
                        v-html="$t('constants.' + category.data.subtitle)"
                    ></p>
                    <div
                        class="itb-epdm-colors-wrapper d-flex justify-content-center flex-wrap mx-auto"
                    >
                        <div
                            class="itb-epdm-color"
                            v-for="surface in category['surfaces']"
                        >
                            <div
                                class="itb-bg itb-lazy-loading-bg"
                                v-lazy:background-image="
                                    surface.image
                                        ? '/uploads/' +
                                          surface.image.path +
                                          '/' +
                                          surface.image.name
                                        : null
                                "
                            ></div>
                            <div class="itb-text">{{ surface.name }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section class="itb-download-list" v-if="pageParams.page_pricelist">
            <h3 class="text-center" style="margin-bottom: 1.4rem">
                {{ $t("epdm.download_our_pricelist") }}
            </h3>
            <div class="container">
                <div class="row mx-auto itb-wrapper-row">
                    <div class="col-12 col-md-6 mx-md-auto">
                        <div class="card w-100 h-100">
                            <div class="card-body d-flex align-items-center">
                                <span>{{ $t("epdm.pricelist") }}</span>
                                <a
                                    :href="
                                        '/uploads/' + pageParams.page_pricelist
                                    "
                                    class="ml-auto"
                                    download
                                >
                                    <span>{{ $t("epdm.download") }}</span>
                                    <img
                                        src="/images/icons/4soft_icon_download_blue.svg"
                                        :alt="
                                            $t('general.button') +
                                                ' ' +
                                                $t('epdm.download')
                                        "
                                        class="itb-icon itb-download-icon"
                                    />
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

export default {
    data() {
        return {
            pageParams: [],
            surfaceCategories: [],
            heroCards: [],
            youtubePermit: false,
            isYoutubeHighlighted: false
        };
    },

    mounted() {
        axios
            .get("/getEPDMPage")
            .then(response => {
                this.pageParams = response.data.params;
                this.heroCards = response.data.heroCards;
                this.surfaceCategories = response.data.surfaceCategories;
                this.setPageParams(this.pageParams);
            })
            .catch(error => {
                console.log(error);
            });
    },

    computed: {
        ...mapGetters({
            appParams: "getAppParams"
        })
    },

    methods: {
        ...mapActions(["setPageParams"]),
        scrollToElement(elementId) {
            this.$parent.scrollToElement(elementId);
        },
        warningHighlight() {
            this.isYoutubeHighlighted = true;
            setTimeout(() => {
                this.isYoutubeHighlighted = false;
            }, 1500);
        }
    }
};
</script>
